/**
 * Attaches a captured referral code to the freshly signed-in user.
 *
 * This must run for EVERY way a new account can land in the app, not just the
 * /auth page: users who confirm their address through the e-mail link come back
 * on `emailRedirectTo` (e.g. /moje-podniky) and would otherwise never have their
 * referral attached.
 */
let inFlight: Promise<void> | null = null;
let attached = false;

/** Reads `?ref=CODE` straight off the current URL (survives cross-device confirm links). */
function refFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const ref = new URLSearchParams(window.location.search).get("ref");
    return ref && /^[A-Za-z0-9]{4,32}$/.test(ref) ? ref.toUpperCase() : null;
  } catch {
    return null;
  }
}

export async function attachStoredReferral(): Promise<void> {
  if (attached || typeof window === "undefined") return;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const { getStoredReferralCode, clearStoredReferralCode } = await import(
        "@/lib/referral-tracker"
      );
      const code = getStoredReferralCode() ?? refFromUrl();
      if (!code) return;

      const { attachReferralOnSignup } = await import("@/lib/referrals.functions");
      try {
        const res = await attachReferralOnSignup({ data: { refCode: code } });
        // Keep the stored code when the failure is transient-looking; drop it
        // once the outcome is final (attached, already set, self, unknown code).
        if (res?.ok || res?.reason !== "window_expired") {
          clearStoredReferralCode();
          attached = true;
        }
      } catch {
        /* network/auth hiccup — retry on the next sign-in event */
      }
    } catch {
      /* ignore */
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}
